import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { PDFBucket, region } from '../utilities/constants';

const BATCH_RESULTS_PREFIX = 'batch-results';

function getS3Client(awsCredentials) {
  return new S3Client({
    region,
    credentials: {
      accessKeyId: awsCredentials.accessKeyId,
      secretAccessKey: awsCredentials.secretAccessKey,
      sessionToken: awsCredentials.sessionToken,
    },
  });
}

async function fetchJsonFromS3(awsCredentials, key) {
  const client = getS3Client(awsCredentials);
  const command = new GetObjectCommand({
    Bucket: PDFBucket,
    Key: key,
  });

  const response = await client.send(command);
  const body = await response.Body.transformToString();
  return JSON.parse(body);
}

/**
 * Fetch the batch index (all batches summary + costs)
 * Returns { batches: [...], costs: {...} | null }
 */
export async function fetchBatchIndex(awsCredentials) {
  const data = await fetchJsonFromS3(awsCredentials, `${BATCH_RESULTS_PREFIX}/index.json`);
  // Support both old format (plain array) and new format (object with batches + costs)
  if (Array.isArray(data)) {
    return { batches: data, costs: null, generated_at: null };
  }
  return { batches: data.batches || [], costs: data.costs || null, generated_at: data.generated_at || null };
}

/**
 * Fetch file details for a specific batch
 * Returns { files: [...], generated_at: string | null }
 */
export async function fetchBatchFiles(awsCredentials, batchId) {
  const data = await fetchJsonFromS3(awsCredentials, `${BATCH_RESULTS_PREFIX}/${batchId}/files.json`);
  // Support both old format (plain array) and new format (object with files + generated_at)
  if (Array.isArray(data)) {
    return { files: data, generated_at: null };
  }
  return { files: data.files || [], generated_at: data.generated_at || null };
}

/**
 * Generate a presigned URL for downloading a remediated PDF from S3
 */
export async function getPresignedPdfUrl(awsCredentials, bucket, key, filename) {
  const client = getS3Client(awsCredentials);
  const command = new GetObjectCommand({
    Bucket: bucket,
    Key: key,
    ResponseContentDisposition: `attachment; filename="${filename}"`,
  });

  return getSignedUrl(client, command, { expiresIn: 3600 });
}

/**
 * Generate a presigned URL for downloading the compliance report ZIP bundle.
 * Returns the URL string, or null if the bundle doesn't exist yet.
 */
export async function fetchComplianceBundleUrl(awsCredentials) {
  const client = getS3Client(awsCredentials);
  const command = new GetObjectCommand({
    Bucket: PDFBucket,
    Key: 'compliance-reports/latest-bundle.zip',
    ResponseContentDisposition: 'attachment; filename="compliance-report.zip"',
  });

  try {
    return await getSignedUrl(client, command, { expiresIn: 3600 });
  } catch {
    return null;
  }
}
